import { ElNotification } from 'element-plus'
import { ref } from 'vue'
import Request from '@/api/httpRequest'

const INITIAL_RECONNECT_DELAY = 1000
const MAX_RECONNECT_DELAY = 30000
const MAX_REMEMBERED_ALERTS = 1000
const TITLE_SCROLL_INTERVAL = 350
const MAIL_ALERT_TITLE_PATTERN = /^收到\d+条新邮件\s*-\s*/

let socket = null
let reconnectTimer = null
let reconnectDelay = INITIAL_RECONNECT_DELAY
let connectionGeneration = 0
let ticketRequestPending = false
let started = false
const pendingAlertCount = ref(0)
let baseDocumentTitle = ''
let titleScrollTimer = null
let titleScrollPosition = 0
let titleScrollDirection = 1
let activeNotification = null
let latestMailAlert = null
const rememberedAlertKeys = new Map()

function getMailAlertKey(alert) {
    const mailMessageId = alert?.mailMessageId
    if (mailMessageId === null || mailMessageId === undefined || mailMessageId === '') {
        return ''
    }
    return `${alert?.userId ?? 'current'}:${mailMessageId}`
}

function rememberMailAlert(alert) {
    const alertKey = getMailAlertKey(alert)
    if (!alertKey) {
        return true
    }
    if (rememberedAlertKeys.has(alertKey)) {
        return false
    }

    rememberedAlertKeys.set(alertKey, true)
    if (rememberedAlertKeys.size > MAX_REMEMBERED_ALERTS) {
        const oldestKey = rememberedAlertKeys.keys().next().value
        rememberedAlertKeys.delete(oldestKey)
    }
    return true
}

function stopTitleScroll() {
    if (titleScrollTimer) {
        clearInterval(titleScrollTimer)
        titleScrollTimer = null
    }
}

function getAlertDocumentTitle() {
    return `收到${pendingAlertCount.value}条新邮件 - ${baseDocumentTitle}`
}

function renderScrollingDocumentTitle() {
    const titleCharacters = Array.from(getAlertDocumentTitle())
    if (titleCharacters.length <= 1) {
        document.title = titleCharacters.join('')
        return
    }

    const position = Math.min(titleScrollPosition, titleCharacters.length - 1)
    document.title = [
        ...titleCharacters.slice(position),
        ...titleCharacters.slice(0, position)
    ].join('')
}

function advanceTitleScroll() {
    const maxPosition = Math.max(0, Array.from(getAlertDocumentTitle()).length - 1)
    if (maxPosition === 0) {
        return
    }

    titleScrollPosition += titleScrollDirection
    if (titleScrollPosition >= maxPosition) {
        titleScrollPosition = maxPosition
        titleScrollDirection = -1
    } else if (titleScrollPosition <= 0) {
        titleScrollPosition = 0
        titleScrollDirection = 1
    }
    renderScrollingDocumentTitle()
}

function updateDocumentTitle(options = {}) {
    if (options.captureBaseTitle) {
        baseDocumentTitle = document.title.replace(MAIL_ALERT_TITLE_PATTERN, '')
    }
    if (!baseDocumentTitle) {
        baseDocumentTitle = document.title.replace(MAIL_ALERT_TITLE_PATTERN, '')
    }

    stopTitleScroll()
    titleScrollPosition = 0
    titleScrollDirection = 1
    if (pendingAlertCount.value <= 0) {
        document.title = baseDocumentTitle
        return
    }

    renderScrollingDocumentTitle()
    titleScrollTimer = setInterval(advanceTitleScroll, TITLE_SCROLL_INTERVAL)
}

function removeActiveNotification(notification) {
    if (!notification || activeNotification !== notification) {
        return
    }
    activeNotification = null
    latestMailAlert = null
    pendingAlertCount.value = 0
    updateDocumentTitle()
}

function clearMailAlerts() {
    const notification = activeNotification
    activeNotification = null
    latestMailAlert = null
    rememberedAlertKeys.clear()
    pendingAlertCount.value = 0
    notification?.close()
    updateDocumentTitle()
}

function buildWebSocketUrl(ticket) {
    const apiBase = (process.env.VUE_APP_BASE_API || '/zkai').trim()
    let url
    if (/^https?:\/\//i.test(apiBase)) {
        const parsed = new URL(apiBase)
        parsed.protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:'
        parsed.pathname = `${parsed.pathname.replace(/\/$/, '')}/ws/mail`
        parsed.search = ''
        parsed.hash = ''
        url = parsed
    } else {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
        const basePath = `/${apiBase.replace(/^\/+|\/+$/g, '')}`
        url = new URL(`${protocol}//${window.location.host}${basePath}/ws/mail`)
    }
    url.searchParams.set('ticket', ticket)
    return url.toString()
}

function closeSocket() {
    if (reconnectTimer) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
    }
    if (socket) {
        const current = socket
        socket = null
        current.onclose = null
        current.onerror = null
        current.onmessage = null
        current.close()
    }
}

async function openMailAlert(notification, alert, router) {
    removeActiveNotification(notification)
    notification.close()

    let location = null
    if (alert?.mailMessageId !== null && alert?.mailMessageId !== undefined) {
        try {
            const response = await Request.getRequest(
                '/api/mail/alert/location',
                { mailMessageId: alert.mailMessageId },
                { hideLoading: true, suppressErrorMessage: true, dedupe: false }
            )
            location = response?.data || null
        } catch (error) {
            location = null
        }
    }

    const query = {
        folderKey: location?.folderKey || 'inbox',
        alertAt: String(Date.now())
    }
    if (alert?.mailMessageId !== null && alert?.mailMessageId !== undefined) {
        query.mailMessageId = String(alert.mailMessageId)
    }
    if (location?.groupId) {
        query.groupId = String(location.groupId)
    }
    if (location?.folderName) {
        query.folderName = String(location.folderName)
    }
    await router.push({ name: 'Email', query })
}

function showMailAlert(alert, router) {
    if (!rememberMailAlert(alert)) {
        return
    }

    latestMailAlert = alert
    pendingAlertCount.value += 1
    updateDocumentTitle()
    if (activeNotification) {
        return
    }

    const notification = ElNotification({
        message: () => `收到 ${pendingAlertCount.value} 封新邮件，点击查看`,
        type: 'info',
        duration: 0,
        position: 'bottom-right',
        onClose: () => removeActiveNotification(notification),
        onClick: () => {
            openMailAlert(notification, latestMailAlert, router).catch(error => {
                console.warn('打开新邮件所在文件夹失败', error)
            })
        }
    })
    activeNotification = notification
}

function scheduleReconnect(store, router, generation) {
    if (generation !== connectionGeneration || !store.state.token || reconnectTimer) {
        return
    }
    reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        connect(store, router, generation)
    }, reconnectDelay)
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY)
}

async function connect(store, router, generation) {
    if (generation !== connectionGeneration || !store.state.token || ticketRequestPending) {
        return
    }
    if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
        return
    }

    ticketRequestPending = true
    try {
        const response = await Request.postRequest(
            '/api/mail/alert/websocket-ticket',
            {},
            { hideLoading: true, suppressErrorMessage: true }
        )
        if (generation !== connectionGeneration || !store.state.token || !response?.data) {
            return
        }

        const current = new WebSocket(buildWebSocketUrl(response.data))
        socket = current
        current.onopen = () => {
            if (socket === current) {
                reconnectDelay = INITIAL_RECONNECT_DELAY
            }
        }
        current.onmessage = event => {
            try {
                showMailAlert(JSON.parse(event.data), router)
            } catch (error) {
                console.warn('新邮件提醒消息格式错误', error)
            }
        }
        current.onerror = () => current.close()
        current.onclose = () => {
            if (socket === current) {
                socket = null
                scheduleReconnect(store, router, generation)
            }
        }
    } catch (error) {
        scheduleReconnect(store, router, generation)
    } finally {
        ticketRequestPending = false
        if (generation !== connectionGeneration && store.state.token) {
            connect(store, router, connectionGeneration)
        }
    }
}

export function startMailAlertSocket(store, router) {
    if (started) {
        return
    }
    started = true

    baseDocumentTitle = document.title.replace(MAIL_ALERT_TITLE_PATTERN, '')
    router.afterEach(() => updateDocumentTitle({ captureBaseTitle: true }))

    store.watch(
        state => state.token,
        (token, previousToken) => {
            connectionGeneration += 1
            closeSocket()
            reconnectDelay = INITIAL_RECONNECT_DELAY
            if (!token || (previousToken && token !== previousToken)) {
                clearMailAlerts()
            }
            if (token) {
                connect(store, router, connectionGeneration)
            }
        },
        { immediate: true }
    )

    window.addEventListener('online', () => {
        if (store.state.token && (!socket || socket.readyState === WebSocket.CLOSED)) {
            connect(store, router, connectionGeneration)
        }
    })
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden && store.state.token && (!socket || socket.readyState === WebSocket.CLOSED)) {
            connect(store, router, connectionGeneration)
        }
    })
}
